// Turf Talk web API client. Shared backend, JWT bearer, refresh on 401.
// The web app and mobile app share the SAME backend + database. All API calls
// target that shared backend origin (NOT the web app's own served origin), so
// authenticated calls work behind the custom-domain edge (turftalk.titanhavengroup.com),
// which only fronts the static frontend and does not route /api/* to the backend.
// Override with REACT_APP_API_BASE_URL if the shared backend host ever changes.
import { buildMentionPayload } from "../lib/roomAsk";
import { buildVoiceTogglePayload } from "../lib/voiceWorker";
const SHARED_BACKEND_ORIGIN = "https://serene-zhukovsky-10.emergent.host";
const ORIGIN = (process.env.REACT_APP_API_BASE_URL || SHARED_BACKEND_ORIGIN).replace(/\/+$/, "");

export const API_ORIGIN = ORIGIN;
const BASE = `${ORIGIN}/api/v1`;

const ACCESS_KEY = "tt.access";
const REFRESH_KEY = "tt.refresh";

export class AuthExpiredError extends Error {}

export function setTokens(access, refresh) {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}
export function getAccess() { return localStorage.getItem(ACCESS_KEY) || ""; }
function getRefresh() { return localStorage.getItem(REFRESH_KEY) || ""; }
export function isAuthed() { return !!getAccess(); }

export function newIdempotencyKey() {
  return `idk-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

async function rawFetch(path, opts, token) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), opts.timeoutMs ?? 15000);
  try {
    return await fetch(`${BASE}${path}`, {
      method: opts.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opts.headers ?? {}),
      },
      body: opts.body != null ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(t);
  }
}

let refreshInFlight = null;
async function rotateSession(failedAccess) {
  // A different request/tab may already have rotated this session.
  if (getAccess() && getAccess() !== failedAccess) return true;
  const refresh = getRefresh();
  if (!refresh) throw new AuthExpiredError("Session expired");
    const res = await rawFetch("/auth/refresh", { method: "POST", body: { refresh_token: refresh } });
    // Never erase a newer login/rotation, or resurrect an explicitly logged-out session.
    if (getRefresh() !== refresh) {
      if (getAccess()) return true;
      throw new AuthExpiredError("Session expired");
    }
    if (res.status === 401 || res.status === 403) {
      clearTokens();
      throw new AuthExpiredError("Session expired");
    }
    if (!res.ok) throw new Error("Session refresh temporarily unavailable. Retry when the connection recovers.");
    const data = await res.json();
    if (getRefresh() !== refresh) {
      if (getAccess()) return true;
      throw new AuthExpiredError("Session expired");
    }
    if (!data.access_token || !data.refresh_token) throw new Error("Invalid session refresh response.");
    setTokens(data.access_token, data.refresh_token);
    return true;
}

function tryRefresh(failedAccess) {
  if (!refreshInFlight) {
    // Web Locks serializes rotation across same-origin tabs without transmitting tokens.
    const rotate = () => rotateSession(failedAccess);
    refreshInFlight = (globalThis.navigator?.locks?.request
      ? navigator.locks.request("tt-session-refresh", rotate)
      : Promise.resolve().then(rotate)).finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

export async function api(path, opts = {}) {
  const token = opts.auth ? getAccess() : undefined;
  let res;
  try {
    res = await rawFetch(path, opts, token);
  } catch (e) {
    if (e?.name === "AbortError") throw new Error("Request timed out. Check your connection and retry.");
    throw new Error("Network error. Check your connection and retry.");
  }
  if (res.status === 401 && opts.auth && !opts._retry) {
    const ok = await tryRefresh(token);
    if (ok) return api(path, { ...opts, _retry: true });
    throw new AuthExpiredError("Session expired");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = (data && (data.detail || data.message)) || "Request failed";
    const err = new Error(typeof detail === "string" ? detail : "Request failed");
    err.status = res.status;
    throw err;
  }
  return data;
}

const qs = (o = {}) =>
  Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");

export const TurfTalk = {
  health: () => api("/health"),
  serverTime: () => api("/time"),
  featureFlags: () => api("/feature-flags"),
  canonicalMeta: () => api("/meta/canonical"),

  register: (body) => api("/auth/register", { method: "POST", body }),
  // Beta invite validation — non-consuming, server-authoritative, no auth. The
  // caller NEVER default-allows: a throw/non-2xx must block registration.
  validateInvite: (code) => api("/auth/invite/validate", { method: "POST", body: { code } }),
  login: (email, password) => api("/auth/login", { method: "POST", body: { email, password } }),
  logout: (refresh_token) => api("/auth/logout", { method: "POST", body: { refresh_token } }),
  me: () => api("/users/me", { auth: true }),
  checkHandle: (handle) => api(`/handles/check?handle=${encodeURIComponent(handle)}`),
  completeOnboarding: (body) => api("/onboarding", { method: "POST", body, auth: true }),
  updateProfile: (body) => api("/users/me", { method: "PATCH", body, auth: true }),
  forgotPassword: (email) => api("/auth/password/forgot", { method: "POST", body: { email } }),
  resetPassword: (token, new_password) => api("/auth/password/reset", { method: "POST", body: { token, new_password } }),

  // Notifications (shared backend contract)
  notifications: (params = {}) => api(`/notifications${qs(params) ? `?${qs(params)}` : ""}`, { auth: true }),
  notificationsUnread: () => api("/notifications/unread-count", { auth: true }),
  notificationRead: (nid) => api(`/notifications/${encodeURIComponent(nid)}/read`, { method: "POST", auth: true }),
  notificationsReadAll: () => api("/notifications/read-all", { method: "POST", auth: true }),
  notificationPreferences: () => api("/notifications/preferences", { auth: true }),
  updateNotificationPreferences: (body) => api("/notifications/preferences", { method: "PUT", body, auth: true }),

  // One-time owner claim. Sends the secret + chosen password ONLY in the JSON
  // body (never URL/storage/logs) and returns a status-coded result WITHOUT any
  // backend detail, so the caller renders plain, non-revealing messages. No auth
  // token is returned or stored.
  ownerClaim: async (secret, chosen_password) => {
    let res;
    try {
      res = await rawFetch("/auth/owner-claim", { method: "POST", body: { secret, chosen_password } });
    } catch {
      const e = new Error("owner_claim_network");
      e.code = "network";
      throw e;
    }
    if (res.ok) return { status: "claimed" };
    const e = new Error("owner_claim_failed");
    e.httpStatus = res.status;
    throw e;
  },

  teams: (league) => api(`/teams${league ? `?league=${league}` : ""}`),
  characters: () => api("/characters"),
  news: (params = {}) => api(`/news${qs(params) ? `?${qs(params)}` : ""}`, { auth: true }),
  games: (params = {}) => api(`/games${qs(params) ? `?${qs(params)}` : ""}`),
  game: (id, auth = true) => api(`/games/${id}`, { auth }),
  plays: (id, skip = 0, limit = 50) => api(`/games/${id}/plays?limit=${limit}&skip=${skip}`, { auth: true }),
  pulse: (id) => api(`/games/${id}/pulse`, { auth: true }),
  odds: (id) => api(`/games/${id}/odds`, { auth: true }),
  picks: (id) => api(`/games/${id}/picks`, { auth: true }),
  home: () => api("/home", { auth: true }),

  // Rooms + messages
  officialRoom: (gameId) => api(`/games/${gameId}/room`, { auth: true }),
  createPrivateRoom: (body) => api("/rooms/private", { method: "POST", body, auth: true }),
  getRoom: (id) => api(`/rooms/${id}`, { auth: true }),
  roomMembers: (id) => api(`/rooms/${id}/members`, { auth: true }),
  leaveRoom: (id) => api(`/rooms/${id}/leave`, { method: "POST", auth: true }),
  createRoomInvite: (id, body) => api(`/rooms/${id}/invites`, { method: "POST", body, auth: true }),
  roomMessages: (id, params = {}) => api(`/rooms/${id}/messages${qs(params) ? `?${qs(params)}` : ""}`, { auth: true }),
  postRoomMessage: (id, body) => api(`/rooms/${id}/messages`, { method: "POST", body, auth: true }),
  reactMessage: (mid, emoji) => api(`/messages/${mid}/react`, { method: "POST", body: { emoji }, auth: true }),
  roomsSummary: () => api("/rooms/summary/active", { auth: true }),

  // AI engagement (shared backend owns all logic: flags, cooldown, guardrails, kill switch).
  roomAiStatus: (id) => api(`/rooms/${id}/ai-status`, { auth: true }),
  roomAiMention: (id, role, idempotencyKey) => api(`/rooms/${id}/ai/mention`, { method: "POST", body: buildMentionPayload(role, { idempotencyKey }), auth: true }),

  // Connections
  discoverHandle: (handle) => api(`/discovery/handle?handle=${encodeURIComponent(handle)}`, { auth: true }),
  requestConnection: (handle) => api("/connections/request", { method: "POST", body: { handle }, auth: true }),
  listConnections: () => api("/connections", { auth: true }),
  connectionInvites: () => api("/connections/invites", { auth: true }),
  acceptConnection: (id) => api(`/connections/invites/${id}/accept`, { method: "POST", auth: true }),
  declineConnection: (id) => api(`/connections/invites/${id}/decline`, { method: "POST", auth: true }),

  // Direct messages
  createConversation: (body) => api("/conversations", { method: "POST", body, auth: true }),
  listConversations: () => api("/conversations", { auth: true }),
  conversationMessages: (id, params = {}) => api(`/conversations/${id}/messages${qs(params) ? `?${qs(params)}` : ""}`, { auth: true }),
  postConversationMessage: (id, body) => api(`/conversations/${id}/messages`, { method: "POST", body, auth: true }),

  // Predictor + leagues
  characterShow: (id) => api(`/games/${id}/character-show`, { auth: true }),
  gamePredictions: (id) => api(`/games/${id}/predictions`, { auth: true }),
  publishCard: (id, body) => api(`/games/${id}/predictions`, { method: "POST", body, auth: true }),
  predictorLeaderboard: (scope = "characters", window = "all", week) =>
    api(`/predictor/leaderboard?scope=${scope}&window=${window}${week != null ? `&week=${week}` : ""}`, { auth: true }),
  myLeagues: () => api("/leagues", { auth: true }),
  createLeague: (body) => api("/leagues", { method: "POST", body, auth: true }),
  joinLeague: (code) => api("/leagues/join", { method: "POST", body: { code }, auth: true }),
  leagueGet: (id) => api(`/leagues/${id}`, { auth: true }),
  leagueLeave: (id) => api(`/leagues/${id}/leave`, { method: "POST", auth: true }),
  leagueRemoveMember: (id, targetId) => api(`/leagues/${id}/members/${targetId}/remove`, { method: "POST", auth: true }),
  leagueTransfer: (id, targetId) => api(`/leagues/${id}/transfer/${targetId}`, { method: "POST", auth: true }),
  leagueRotateInvite: (id) => api(`/leagues/${id}/invite/rotate`, { method: "POST", auth: true }),
  leagueRedeem: (token) => api("/leagues/invites/redeem", { method: "POST", body: { token }, auth: true }),

  // Invite-link resolution (deep links)
  resolveRoomInvite: (token) => api("/rooms/invites/resolve", { method: "POST", body: { token }, auth: true }),
  resolveTailgateInvite: (token) => api("/tailgates/invites/resolve", { method: "POST", body: { token }, auth: true }),
  resolveLiveInvite: (token) => api("/livestreams/invites/resolve", { method: "POST", body: { token }, auth: true }),

  // Commerce (sandbox / billing deferred — no live charges)
  shopProducts: () => api("/commerce/products", { auth: true }),
  shopProduct: (id) => api(`/commerce/products/${id}`, { auth: true }),
  cartGet: () => api("/commerce/cart", { auth: true }),
  cartAdd: (body) => api("/commerce/cart/items", { method: "POST", body, auth: true }),
  cartRemove: (sku) => api(`/commerce/cart/items/${sku}`, { method: "DELETE", auth: true }),
  checkout: (body) => api("/commerce/checkout", { method: "POST", body, auth: true }),
  orders: () => api("/commerce/orders", { auth: true }),
  entitlement: () => api("/commerce/entitlement", { auth: true }),
  plusTiers: () => api("/commerce/plus/tiers", { auth: true }),
  membershipStart: (platform = "web") => api("/commerce/membership/start", { method: "POST", body: { platform }, auth: true }),

  // Moderation
  moderationReports: () => api("/moderation/reports", { auth: true }),
  moderationAction: (body) => api("/moderation/action", { method: "POST", body, auth: true }),
  createReport: (body) => api("/reports", { method: "POST", body, auth: true }),
  listBlocks: () => api("/blocks", { auth: true }),
  blockUser: (targetId) => api(`/blocks/${targetId}`, { method: "POST", auth: true }),
  unblockUser: (targetId) => api(`/blocks/${targetId}`, { method: "DELETE", auth: true }),

  trophyCabinet: () => api("/achievements", { auth: true }),

  // Tailgates + livestreams
  tailgateList: (q = {}) => api(`/tailgates${qs(q) ? `?${qs(q)}` : ""}`, { auth: true }),
  tailgateCreate: (body) => api("/tailgates", { method: "POST", body, auth: true }),
  tailgateCancel: (id) => api(`/tailgates/${id}/cancel`, { method: "POST", auth: true }),
  tailgateLeave: (id) => api(`/tailgates/${id}/leave`, { method: "POST", auth: true }),
  tailgateGet: (id) => api(`/tailgates/${id}`, { auth: true }),
  tailgateJoin: (id) => api(`/tailgates/${id}/join`, { method: "POST", auth: true }),
  liveMeta: () => api("/livestreams/meta", { auth: true }),
  liveList: (q = {}) => api(`/livestreams${qs(q) ? `?${qs(q)}` : ""}`, { auth: true }),
  liveCreate: (body) => api("/livestreams", { method: "POST", body, auth: true }),
  liveStart: (id) => api(`/livestreams/${id}/start`, { method: "POST", auth: true }),
  liveCancel: (id) => api(`/livestreams/${id}/cancel`, { method: "POST", auth: true }),
  liveEnd: (id) => api(`/livestreams/${id}/end`, { method: "POST", auth: true }),
  liveApproveCohost: (id, userId) => api(`/livestreams/${encodeURIComponent(id)}/cohosts/${encodeURIComponent(userId)}`, { method: "POST", auth: true }),
  liveGet: (id) => api(`/livestreams/${id}`, { auth: true }),
  // Short-lived least-privilege LiveKit participant token (fresh per join).
  liveToken: (id) => api(`/livestreams/${id}/token`, { method: "POST", auth: true }),
  liveLeave: (id) => api(`/livestreams/${id}/leave`, { method: "POST", auth: true }),
  liveReactions: (id) => api(`/livestreams/${id}/reactions/summary`, { auth: true }),
  liveReact: (id, emoji) => api(`/livestreams/${id}/reactions?emoji=${encodeURIComponent(emoji)}`, { method: "POST", auth: true }),

  // Stage 6 recorded media (init -> chunk -> complete; signed playback).
  mediaInit: (body) => api(`/media/init`, { method: "POST", body, auth: true }),
  mediaComplete: (id) => api(`/media/${id}/complete`, { method: "POST", auth: true }),
  mediaMeta: (id) => api(`/media/${id}`, { auth: true }),
  mediaDelete: (id) => api(`/media/${id}`, { method: "DELETE", auth: true }),
  mediaPublish: (id, body) => api(`/media/${id}/publish`, { method: "POST", body, auth: true }),

  // Admin / operator
  adminUsers: (q) => api(`/admin/users${qs(q) ? `?${qs(q)}` : ""}`, { auth: true }),
  // Beta invite-code management (admin-only; the shared backend enforces the
  // is_admin authorization — the UI gate is cosmetic, not the control).
  adminBetaCodes: () => api("/admin/beta-codes", { auth: true }),
  adminCreateBetaCode: (body) => api("/admin/beta-codes", { method: "POST", body, auth: true }),
  adminRevokeBetaCode: (id) => api(`/admin/beta-codes/${encodeURIComponent(id)}/revoke`, { method: "POST", auth: true }),
  adminProviderHealth: () => api("/admin/providers/health", { auth: true }),
  adminCharacterScripts: (q = {}) => api(`/admin/characters/scripts${qs(q) ? `?${qs(q)}` : ""}`, { auth: true }),
  adminScriptAction: (id, action, body = {}) => api(`/admin/characters/scripts/${id}/${action}`, { method: "POST", body, auth: true }),
  adminGlobalKill: (on) => api(`/admin/characters/kill-switch?on=${on}`, { method: "POST", auth: true }),
  schedulerStatus: () => api("/admin/characters/scheduler", { auth: true }),
  schedulerRunOnce: () => api("/admin/characters/scheduler/run-once", { method: "POST", auth: true }),
  schedulerPause: () => api("/admin/characters/scheduler/pause", { method: "POST", auth: true }),
  schedulerResume: () => api("/admin/characters/scheduler/resume", { method: "POST", auth: true }),

  // Character Show branded-audio worker — status + on/off dashboard switch.
  // The toggle POSTs ONLY { enabled } so the daily cap and automatic-pregame
  // preference are preserved by the backend. No generate/run-once/preview here.
  adminVoiceWorker: () => api("/admin/characters/voices", { auth: true }),
  adminVoiceWorkerSetEnabled: (on) => api("/admin/characters/voices/config", { method: "POST", body: buildVoiceTogglePayload(on), auth: true }),

  // Admin AI engagement — status + safe control (kill / resume).
  adminAiConfig: () => api("/admin/ai/config", { auth: true }),
  // Operator-only read-only AI fallback diagnostics. `before` is an opaque
  // keyset cursor — pass next_before back unchanged (qs URL-encodes it).
  adminAiDiagnostics: (params = {}) => api(`/admin/ai/diagnostics${qs(params) ? `?${qs(params)}` : ""}`, { auth: true }),
  adminAiKill: () => api("/admin/ai/kill", { method: "POST", auth: true }),
  adminAiResume: () => api("/admin/ai/resume", { method: "POST", auth: true }),
  // Unified operator overview (bounded, admin-only). May be absent until the
  // shared backend publishes it; the console adapter composes a fallback.
  adminOpsOverview: () => api("/admin/ops/overview", { auth: true }),

  // Phase 2 telemetry (first-party, privacy-safe; body carries no PII/content).
  telemetryEvents: (events) => api("/telemetry/events", { method: "POST", body: { events }, auth: true }),
  adminTelemetryMetrics: (window = "7d") => api(`/admin/telemetry/metrics?window=${encodeURIComponent(window)}`, { auth: true }),
  adminOpsReadiness: (gameId) => api(`/admin/ops/readiness${gameId ? `?game_id=${encodeURIComponent(gameId)}` : ""}`, { auth: true }),
};

export function mediaContentUrl(contentUrl) { return `${ORIGIN}${contentUrl}`; }

// Chunked upload: init -> PUT raw chunks -> complete. Never invents endpoints;
// mirrors the shared backend Stage 6 contract. Returns the completed media doc.
export async function uploadMedia({ file, kind, contextType = "upload", contextRefId = null, durationSeconds = null, onProgress, signal }) {
  const init = await TurfTalk.mediaInit({
    kind,
    declared_duration_seconds: durationSeconds,
    context_type: contextType,
    context_ref_id: contextRefId,
  });
  const mediaId = init.media_id;
  const chunkSize = init.chunk_size || 1024 * 1024;
  const total = file.size;
  let offset = 0;
  let index = 0;
  const token = getAccess();
  while (offset < total) {
    if (signal?.aborted) { try { await TurfTalk.mediaDelete(mediaId); } catch (_) {} throw new DOMException("Upload cancelled", "AbortError"); }
    const slice = file.slice(offset, Math.min(offset + chunkSize, total));
    let res;
    try {
      res = await fetch(`${BASE}/media/${mediaId}/chunk?index=${index}`, {
        method: "PUT",
        headers: { "Content-Type": "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: slice,
        signal,
      });
    } catch (netErr) {
      if (netErr?.name === "AbortError") { try { await TurfTalk.mediaDelete(mediaId); } catch (_) {} throw netErr; }
      const e = new Error(kind === "video" ? "Video upload isn’t available right now — please retry." : "Upload failed — please retry.");
      e.mediaId = mediaId; e.retryable = true; throw e;
    }
    if (!res.ok) {
      let msg = kind === "video" ? "Video upload isn’t available right now — please retry." : "Upload failed — please retry.";
      try { const j = await res.json(); if (j.detail || j.message) msg = j.detail || j.message; } catch (_) {}
      const err = new Error(msg); err.mediaId = mediaId; err.retryable = true; throw err;
    }
    offset += chunkSize; index += 1;
    onProgress?.(Math.min(1, offset / total));
  }
  return TurfTalk.mediaComplete(mediaId);
}
